// src/context/ReactDevTools.jsx
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const ReactDevTools = createContext();

export const useReactDevTools = () => {
  const context = useContext(ReactDevTools);
  if (!context) {
    throw new Error('useReactDevTools must be used within ReactDevToolsProvider');
  }
  return context;
};

export const ReactDevToolsProvider = ({ children }) => {
  // ✅ Default from localStorage — login will control this
  const [isSecretMode, setIsSecretMode] = useState(() => {
    try {
      return localStorage.getItem('isSecretMode') === 'true';
    } catch {
      return false;
    }
  });

  const [secretFeatures, setSecretFeatures] = useState({
    showDevTools: false,
    showDebugInfo: false,
    showAdminActions: false,
    showPerformanceMetrics: false,
    showSecretMenu: false,
  });

  const shakeCount = useRef(0);
  const lastShakeTime = useRef(0);
  const shakeTimeout = useRef(null);
  const isShakeActivated = useRef(false);

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
      setIsMobile(mobileRegex.test(userAgent));
    };
    checkMobile();
  }, []);

  const toggleSecretMode = useCallback(() => {
    setIsSecretMode(prev => {
      const newMode = !prev;
      try { localStorage.setItem('isSecretMode', String(newMode)); } catch {}
      return newMode;
    });
    isShakeActivated.current = false;
  }, []);

  const handleSecretActivation = useCallback(() => {
    if (!isMobile) {
      toggleSecretMode();
      return;
    }
    console.log('📱 Click detected! Now shake your device to enable secret mode...');
    isShakeActivated.current = true;
    shakeCount.current = 0;

    if (shakeTimeout.current) clearTimeout(shakeTimeout.current);
    shakeTimeout.current = setTimeout(() => {
      isShakeActivated.current = false;
      shakeCount.current = 0;
    }, 5000);
  }, [isMobile, toggleSecretMode]);

  // Shake detection for mobile (kept as fallback)
  useEffect(() => {
    if (!isMobile) return;

    const handleMotion = (event) => {
      const acceleration = event.accelerationIncludingGravity;
      if (!acceleration) return;

      const magnitude = Math.sqrt(
        (acceleration.x || 0) ** 2 +
        (acceleration.y || 0) ** 2 +
        (acceleration.z || 0) ** 2
      );

      const SHAKE_THRESHOLD = 20;
      if (magnitude > SHAKE_THRESHOLD && isShakeActivated.current) {
        const currentTime = Date.now();
        if (currentTime - lastShakeTime.current > 500) {
          shakeCount.current += 1;
          lastShakeTime.current = currentTime;
          if (shakeCount.current >= 3) {
            toggleSecretMode();
            isShakeActivated.current = false;
            shakeCount.current = 0;
            if (shakeTimeout.current) clearTimeout(shakeTimeout.current);
          }
        }
      }
    };

    if (typeof DeviceMotionEvent !== 'undefined' && typeof DeviceMotionEvent.requestPermission === 'function') {
      DeviceMotionEvent.requestPermission()
        .then(state => {
          if (state === 'granted') window.addEventListener('devicemotion', handleMotion);
        })
        .catch(console.error);
    } else {
      window.addEventListener('devicemotion', handleMotion);
    }

    return () => {
      window.removeEventListener('devicemotion', handleMotion);
      if (shakeTimeout.current) clearTimeout(shakeTimeout.current);
    };
  }, [isMobile, toggleSecretMode]);

  // Keyboard shortcut (Ctrl+Shift+X) — still works as runtime toggle
  useEffect(() => {
    const handleKeyDown = (event) => {
      const isCtrl = event.ctrlKey || event.metaKey;
      const isShift = event.shiftKey;
      const isX = event.key === 'x' || event.key === 'X';

      if (isCtrl && isShift && isX) {
        event.preventDefault();
        toggleSecretMode();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleSecretMode]);

  const toggleFeature = useCallback((featureName) => {
    setSecretFeatures(prev => ({ ...prev, [featureName]: !prev[featureName] }));
  }, []);

  // ✅ Persist on enable
  const enableSecretMode = useCallback(() => {
    setIsSecretMode(true);
    try { localStorage.setItem('isSecretMode', 'true'); } catch {}
  }, []);

  // ✅ Persist on disable
  const disableSecretMode = useCallback(() => {
    setIsSecretMode(false);
    try { localStorage.setItem('isSecretMode', 'false'); } catch {}
    setSecretFeatures({
      showDevTools: false,
      showDebugInfo: false,
      showAdminActions: false,
      showPerformanceMetrics: false,
      showSecretMenu: false,
    });
  }, []);

  const value = {
    isSecretMode,
    secretFeatures,
    toggleSecretMode,
    toggleFeature,
    enableSecretMode,
    disableSecretMode,
    handleSecretActivation,
    isMobile,
  };

  return (
    <ReactDevTools.Provider value={value}>
      {children}
    </ReactDevTools.Provider>
  );
};